import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useEvent } from '@/context/EventContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  ChevronLeft, ChevronRight, Calendar as CalendarIcon,
  Trophy, Users, Settings, Eye, Check, Loader2, Layers, Sparkles
} from 'lucide-react';
import { readConversion, canImportScores, finishConversion } from '@/lib/bracket/convert';
import { bracketFormatsFor } from '@/lib/bracket/templates';

import { EventInfoStep } from '@/components/wizard/EventInfoStep';
import { TeamsStep } from '@/components/wizard/TeamsStep';
import { PoolsStep } from '@/components/wizard/PoolsStep';
import { FormatStep } from '@/components/wizard/FormatStep';
import { RulesStep } from '@/components/wizard/RulesStep';
import { PlayoffsStep } from '@/components/wizard/PlayoffsStep';
import { ReviewStep } from '@/components/wizard/ReviewStep';

const STEPS = [
  { id: 1, title: 'Event Info', icon: CalendarIcon },
  { id: 2, title: 'Teams', icon: Users },
  { id: 3, title: 'Pools', icon: Layers },
  { id: 4, title: 'Format', icon: Trophy },
  { id: 5, title: 'Rules', icon: Settings },
  { id: 6, title: 'Playoffs', icon: Trophy },
  { id: 7, title: 'Review', icon: Eye }
];

export default function EventWizard() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [draft] = useState(() => (params.get('from') === 'bracket' ? readConversion() : null));
  const { createEvent, loading } = useEvent();
  const { isAuthenticated, isPro } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [errors, setErrors] = useState({});

  const formatRequiresAuth = useCallback((fmt) => fmt !== 'round_robin', []);

  const [eventData, setEventData] = useState(() => ({
    name: draft?.name || '',
    sport: 'basketball',
    start_date: draft?.date ? new Date(`${draft.date}T00:00:00`) : new Date(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    venue: '',
    num_courts: 1,
    game_days: [0, 1, 2, 3, 4, 5, 6],
    blackout_dates: '',
    teams: draft?.teams || [],
    format: draft?.format || 'round_robin',
    bracket_tournament: !!draft,
    bracket_positions: null,
    import_confirmed: false,
    rules: {
      games_per_day: 4,
      game_duration: 60,
      buffer_time: 15,
      min_rest_time: 60,
      no_back_to_back: false,
      first_game_time: '09:00',
      game_times: [],
      avoid_times: [],
      game_days: [0, 1, 2, 3, 4, 5, 6],
      blackout_dates: [],
      home_away_enabled: false
    },
    playoffs: {
      enabled: false,
      teams_advance: 4,
      bracket_type: 'single',
      seeding_method: 'record',
      consolation_bracket: false,
      teams_per_pool: 2
    },
    pool_config: {
      enabled: false,
      num_pools: 2,
      pools: []
    }
  }));

  const generatedGameTimes = useMemo(() => {
    const { first_game_time, games_per_day, game_duration, buffer_time } = eventData.rules;
    const times = [];
    try {
      const [firstHour, firstMin] = first_game_time.split(':').map(Number);
      const interval = game_duration + buffer_time;
      for (let i = 0; i < games_per_day; i++) {
        const total = firstHour * 60 + firstMin + (i * interval);
        times.push(`${Math.floor(total / 60).toString().padStart(2, '0')}:${(total % 60).toString().padStart(2, '0')}`);
      }
    } catch {
      for (let i = 0; i < games_per_day; i++) times.push(`${(9 + i * 2).toString().padStart(2, '0')}:00`);
    }
    return times;
  }, [eventData.rules.first_game_time, eventData.rules.games_per_day, eventData.rules.game_duration, eventData.rules.buffer_time]);

  const validateStep = useCallback((step) => {
    const newErrors = {};
    switch (step) {
      case 1:
        if (!eventData.name.trim()) newErrors.name = 'Event name is required';
        if (!eventData.start_date) newErrors.start_date = 'Start date is required';
        if (eventData.game_days.length === 0) newErrors.game_days = 'Select at least one game day';
        break;
      case 2:
        if (eventData.teams.length < 2) newErrors.teams = 'At least 2 teams are required';
        break;
      case 3:
        if (eventData.pool_config.enabled) {
          const assigned = eventData.pool_config.pools.reduce((acc, p) => acc + p.team_ids.length, 0);
          if (assigned !== eventData.teams.length) newErrors.pools = `All teams must be assigned (${assigned}/${eventData.teams.length})`;
          if (eventData.pool_config.pools.some(p => p.team_ids.length < 2)) newErrors.pools = 'Each pool must have at least 2 teams';
        }
        break;
      case 4:
        if (!eventData.format) newErrors.format = 'Please select a format';
        if (eventData.bracket_tournament && !bracketFormatsFor(eventData.teams.length).find((o) => o.format === eventData.format)?.ok) {
          newErrors.format = 'Pick a bracket format that fits your number of teams';
        }
        break;
      case 5:
        if (eventData.rules.games_per_day < 1) newErrors.games_per_day = 'At least 1 game per day';
        if (!eventData.rules.first_game_time) newErrors.first_game_time = 'First game start time is required';
        break;
      default: break;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [eventData]);

  const handleNext = () => {
    if (!validateStep(currentStep)) return;
    if (currentStep === 4 && formatRequiresAuth(eventData.format) && !isAuthenticated) { setShowAuthPrompt(true); return; }
    if (currentStep === 3 && eventData.pool_config.enabled && !isAuthenticated) { setShowAuthPrompt(true); return; }
    if (currentStep === 2 && eventData.teams.length < 4) setCurrentStep(4);
    else if (currentStep === 5 && !['round_robin', 'double_round_robin', 'hybrid'].includes(eventData.format)) setCurrentStep(7);
    else setCurrentStep(prev => Math.min(prev + 1, 7));
  };

  const handleBack = () => {
    setShowAuthPrompt(false);
    if (currentStep === 7 && !['round_robin', 'double_round_robin', 'hybrid'].includes(eventData.format)) setCurrentStep(5);
    else if (currentStep === 4 && eventData.teams.length < 4) setCurrentStep(2);
    else setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(6)) return;
    try {
      const { bracket_tournament, import_confirmed, ...rest } = eventData;
      const isBracketFormat = ['single_elimination', 'double_elimination', 'consolation'].includes(rest.format);
      const payload = {
        ...rest,
        start_date: format(eventData.start_date, 'yyyy-MM-dd'),
        rules: { ...eventData.rules, game_times: generatedGameTimes },
        bracket_positions: isBracketFormat ? rest.bracket_positions : null,
        ...(draft ? {
          bracket_source_id: draft.sourceId,
          bracket_share_id: draft.shareId || null,
          import_scores: import_confirmed && canImportScores(draft, eventData) ? draft.scores : null,
        } : {}),
      };
      const result = await createEvent(payload);
      if (draft) finishConversion(draft, result.event.id);
      if (result.existing) toast.info('This bracket is already a tournament', { description: 'Opening the one you created before.' });
      else if (result.conflicts?.length > 0) toast.warning(`Schedule created with ${result.conflicts.length} conflict(s)`);
      else if (result.imported_scores) toast.success(`Tournament created with ${result.imported_scores} official result${result.imported_scores === 1 ? '' : 's'}`);
      else toast.success('Event created successfully!');
      if (isPro && !result.existing) {
        toast.success(
          <div className="flex flex-col gap-1">
            <span className="font-medium">Saved to My Schedules</span>
            <a href="/my-schedules" className="text-sm text-blue-600 hover:underline">View all schedules</a>
          </div>,
          { duration: 5000 }
        );
      }
      navigate(`/schedule/${result.event.id}${!isAuthenticated ? '?welcome=1' : ''}`);
    } catch (err) {
      toast.error(err.message || 'Failed to create event');
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1: return <EventInfoStep eventData={eventData} setEventData={setEventData} errors={errors} />;
      case 2: return <TeamsStep eventData={eventData} setEventData={setEventData} errors={errors}
                       newTeamName={newTeamName} setNewTeamName={setNewTeamName}
                       isAuthenticated={isAuthenticated} isPro={isPro} />;
      case 3: return <PoolsStep eventData={eventData} setEventData={setEventData} errors={errors} />;
      case 4: return <FormatStep eventData={eventData} setEventData={setEventData}
                       isAuthenticated={isAuthenticated} showAuthPrompt={showAuthPrompt}
                       setShowAuthPrompt={setShowAuthPrompt} />;
      case 5: return <RulesStep eventData={eventData} setEventData={setEventData} generatedGameTimes={generatedGameTimes} />;
      case 6: return <PlayoffsStep eventData={eventData} setEventData={setEventData} />;
      case 7: return <ReviewStep eventData={eventData} setEventData={setEventData} generatedGameTimes={generatedGameTimes} draft={draft} />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="glass-effect sticky top-0 z-50 border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Button variant="ghost" data-testid="back-to-home-btn" onClick={() => navigate('/')} className="text-slate-600 hover:text-slate-800">
              <ChevronLeft className="w-5 h-5 mr-1" />Back
            </Button>
            <h1 className="text-xl font-bold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>CREATE EVENT</h1>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/import')}
              className="text-[#00247D] hover:bg-[#00247D]/5 hidden sm:inline-flex"
              data-testid="wizard-import-pdf-link"
            >
              <Sparkles className="w-4 h-4 mr-1" />
              Import Schedule
            </Button>
            <span className="w-20 sm:hidden" aria-hidden="true" />
          </div>
        </div>
      </header>

      <div className="bg-white border-b border-slate-200 py-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {STEPS.map((step, i) => {
              if (step.id === 3 && eventData.teams.length < 4) return null;
              if (step.id === 6 && !['round_robin', 'double_round_robin', 'hybrid'].includes(eventData.format)) return null;
              const isCompleted = currentStep > step.id;
              const isActive = currentStep === step.id;
              return (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center">
                    <div className={`stepper-circle ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}>
                      {isCompleted ? <Check className="w-5 h-5" /> : step.id}
                    </div>
                    <span className={`text-xs mt-2 hidden sm:block ${isActive ? 'text-[#00247D] font-medium' : 'text-slate-500'}`}>
                      {step.title}
                    </span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`stepper-line ${isCompleted ? 'completed' : ''}`}></div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {draft && (
          <div className="mb-4 rounded-xl border border-[#00247D]/20 bg-white p-4 flex items-start gap-3 animate-in fade-in duration-300" data-testid="wizard-bracket-source-banner">
            <Trophy className="w-5 h-5 text-[#CF142B] shrink-0 mt-0.5" />
            <p className="text-sm text-slate-700">
              <span className="font-semibold text-[#00247D]">Managing your Bracket Library bracket as a tournament.</span>{' '}
              The title, date, {draft.teams.length} teams, seeds and format are filled in. Pick the sport, venues, courts and game times, then review and create.
            </p>
          </div>
        )}
        {errors.format && currentStep === 4 && <p className="mb-3 text-sm text-[#CF142B]" data-testid="wizard-format-error">{errors.format}</p>}
        <Card className="border-slate-200 shadow-card">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>
              {STEPS.find(s => s.id === currentStep)?.title.toUpperCase()}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {renderStepContent()}
          </CardContent>
        </Card>

        <div className="flex justify-between mt-6">
          <Button variant="outline" data-testid="wizard-back-btn" onClick={handleBack} disabled={currentStep === 1} className="h-12 px-6">
            <ChevronLeft className="w-5 h-5 mr-1" />Back
          </Button>
          {currentStep < 7 ? (
            <Button data-testid="wizard-next-btn" onClick={handleNext} className="bg-[#00247D] hover:bg-[#001A5C] h-12 px-6">
              Next<ChevronRight className="w-5 h-5 ml-1" />
            </Button>
          ) : (
            <Button data-testid="wizard-create-btn" onClick={handleSubmit} disabled={loading} className="bg-[#009E49] hover:bg-[#008840] h-12 px-8">
              {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Creating...</> : <><Check className="w-5 h-5 mr-2" />Create Event</>}
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
